/** @jsxImportSource @emotion/react */

import AutoOrientationService from '../../../services/autoOrientation';
import { LinearLayout } from '../LinearLayout/LinearLayout';
import { Text } from '../Text/Text';
import React, { ReactNode } from 'react';
import { useTheme } from '../../ThemeProvider/ThemeProvider';
import { Icon } from '../../Icon/Icon';
import { CSSProp } from '../../../types/emotion';

export interface SideNavigationButtonProps {
  /**
   * The icon to display in the button.
   */
  icon?: ReactNode;
  /**
   * The link to navigate to when the button is clicked.
   */
  link?: string;
  /**
   * The external link to navigate to when the button is clicked.
   */
  externalLink?: string;
  /**
   * Click handler
   */
  onClick?: () => void;
  /**
   * Callback to close the side navigation on mobile.
   */
  onMobileNavigationClose?: () => void;
  /**
   * Whether the button is disabled.
   */
  disabled?: boolean;
  /**
   * The content of the button.
   */
  children?: ReactNode;
  /**
   * Render as different element type than default button.
   */
  as?: any;
  /**
   * Target attribute for link
   */
  target?: string;
}

export function SideNavigationButton({
  icon,
  link,
  externalLink,
  onClick,
  onMobileNavigationClose,
  disabled,
  children,
  as,
  target = '_self',
  ...props
}: SideNavigationButtonProps &
  Omit<Partial<React.HTMLAttributes<HTMLButtonElement>>, keyof SideNavigationButtonProps> &
  CSSProp) {
  const { tokens } = useTheme();

  const ChildrenComponent = typeof children === 'string' ? Text : 'div';

  const ButtonComponent = as ?? (link || externalLink ? 'a' : 'button');

  return (
    <ButtonComponent
      disabled={disabled}
      css={{
        display: 'flex',
        border: 'none',
        background: 'none',
        textDecoration: 'none',
        margin: 0,
        padding: '0.75rem',
        cursor: disabled ? 'default' : 'pointer',
        width: '100%',
        textAlign: 'left',
        '&:hover': {
          background: disabled ? 'inherit' : tokens.colors['secondary-base-700'],
          '.side-navigation-light &': {
            background: disabled ? 'inherit' : tokens.colors['bosch-blue-90'],
          },
        },
        '&:active': {
          background: disabled ? 'inherit' : tokens.colors['secondary-base-600'],
          '.side-navigation-light &': {
            background: disabled ? 'inherit' : tokens.colors['bosch-blue-80'],
          },
        },
      }}
      onClick={() => {
        if (AutoOrientationService.isMobile()) {
          onMobileNavigationClose?.();
        }
        onClick?.();
      }}
      to={link}
      href={externalLink}
      target={target}
      {...props}
    >
      <LinearLayout orientation="horizontal" spacing="small" align="center" css={{ width: '100%' }}>
        {typeof icon === 'string' ? (
          <>
            <Icon
              value={icon}
              size="lg"
              color={disabled ? tokens.colors['secondary-base-600'] : 'white'}
              css={{ '.side-navigation-light &': { display: 'none' } }}
            />
            <Icon
              value={icon}
              size="lg"
              color={disabled ? tokens.colors['secondary-base-600'] : 'black'}
              css={{ '.side-navigation-dark &': { display: 'none' } }}
            />
          </>
        ) : (
          icon
        )}
        {children && (
          <ChildrenComponent
            css={{
              color: disabled ? tokens.colors['secondary-base-600'] : 'white',
              flex: 1,
              whiteSpace: 'nowrap !important',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              p: {
                whiteSpace: 'nowrap !important',
              },
              '.side-navigation-light &': {
                color: disabled ? tokens.colors['secondary-base-600'] : 'black',
              },
            }}
          >
            {children}
          </ChildrenComponent>
        )}
      </LinearLayout>
    </ButtonComponent>
  );
}
